import { URL_PATTERN } from './telemetry-redaction';

/**
 * Attributes a browser error/log to our code or to a third-party script, so recurring vendor
 * failures (CSP-blocked marketing tags, Split/FullStory/Segment network errors) can be measured
 * and filtered separately from first-party application errors. `first_party` means "not
 * attributable to a third party": unknown sources stay visible rather than being hidden.
 */
export type TelemetryAttribution =
  { party: 'first_party' } | { party: 'third_party'; vendor: string; host?: string };

export const FIRST_PARTY: TelemetryAttribution = { party: 'first_party' };

// Our apps and APIs, plus the S3 buckets our backends issue presigned upload/download URLs for.
const FIRST_PARTY_HOSTS = [
  /(^|\.)theguarantors\.com$/,
  /^localhost$/,
  /(^|\.)s3[.-][\w.-]*amazonaws\.com$/,
];

const VENDOR_DOMAINS: Record<string, string[]> = {
  split: ['split.io'],
  fullstory: ['fullstory.com'],
  segment: ['segment.io', 'segment.com'],
  infinitewatch: ['infinitewatch.ai', 'iw-backend-live.fly.dev'],
  userpilot: ['userpilot.io'],
  hubspot: [
    'hubspot.com',
    'hubspot.net',
    'hubapi.com',
    'hs-scripts.com',
    'hs-analytics.net',
    'hs-banner.com',
    'hsadspixel.net',
    'hscollectedforms.net',
    'hsforms.com',
    'hsforms.net',
    'hsappstatic.net',
  ],
  google: [
    'google.com',
    'googleapis.com',
    'gstatic.com',
    'googletagmanager.com',
    'google-analytics.com',
    'googleadservices.com',
    'doubleclick.net',
  ],
  linkedin: ['linkedin.com', 'licdn.com'],
  meta: ['facebook.com', 'facebook.net'],
  cookieyes: ['cookieyes.com', 'cdn-cookieyes.com'],
  zoominfo: ['zoominfo.com'],
  demandbase: ['demandbase.com', 'company-target.com'],
  '6sense': ['6sc.co'],
  twik: ['twik.io'],
  vector: ['vector.co'],
};

// Vendor SDKs that log without a URL in the message or stack.
const VENDOR_MESSAGES: [RegExp, string][] = [
  [/FullStory/, 'fullstory'],
  [/Google Tag Manager|google\.maps\.|Google Maps JavaScript API/, 'google'],
  [/segment performance metrics|Failed to load Remote Plugin/, 'segment'],
  [/\[Meta Pixel\]/, 'meta'],
  [/@firebase\//, 'firebase'],
  // Browsers hide cross-origin script errors behind this message; our bundle is same-origin.
  [/^(Uncaught )?"?Script error\.?"?$/, 'cross_origin_script'],
];

const isFirstPartyHost = (host: string) =>
  host === globalThis.location.hostname || FIRST_PARTY_HOSTS.some((pattern) => pattern.test(host));

const findVendor = (host: string) =>
  Object.keys(VENDOR_DOMAINS).find((vendor) =>
    VENDOR_DOMAINS[vendor].some((domain) => host === domain || host.endsWith(`.${domain}`)),
  );

const attributeUrl = (url: string): TelemetryAttribution => {
  let parsed: URL;
  try {
    parsed = new URL(url, globalThis.location.href);
  } catch {
    return FIRST_PARTY;
  }
  if (parsed.protocol.endsWith('-extension:')) {
    return { party: 'third_party', vendor: 'browser_extension' };
  }
  const host = parsed.hostname;
  if (isFirstPartyHost(host)) {
    return FIRST_PARTY;
  }
  return { party: 'third_party', vendor: findVendor(host) ?? 'other', host };
};

// Non-global copy: `exec` on a `/g` regex is stateful (`lastIndex`) across calls.
const FIRST_URL_PATTERN = new RegExp(URL_PATTERN.source);

/**
 * `url` is the request URL of a network failure; `texts` are the message and stack, in that order.
 * The request URL wins (a network log's stack is the response body, which can mention anything),
 * then a known vendor message, then the first URL in the message (e.g. a CSP-blocked script), then
 * the top stack frame that has a URL.
 */
export const attributeTelemetry = ({
  url,
  texts,
}: {
  url?: string;
  texts: (string | undefined)[];
}): TelemetryAttribution => {
  if (url) {
    return attributeUrl(url);
  }
  const present = texts.filter((text): text is string => Boolean(text));
  const vendorMessage = VENDOR_MESSAGES.find(([pattern]) =>
    present.some((text) => pattern.test(text)),
  );
  if (vendorMessage) {
    return { party: 'third_party', vendor: vendorMessage[1] };
  }
  const textUrl = present.map((text) => FIRST_URL_PATTERN.exec(text)?.[0]).find(Boolean);
  return textUrl ? attributeUrl(textUrl) : FIRST_PARTY;
};
